import { useState, useEffect } from "react";
import { AppContainer } from "@/components/layout/app-container";
import { DemonstrativoExtratoBancarioHeaderButtons } from "../components/demonstrativo-extrato-bancario-header-buttons";
import { TabsUnderline } from "@/components/ui/tabs-underline";
import { BankStatementTableExtrato } from "@/components/bank-statement/bank-statement-table-extrato";
import { DemonstrativoExtratoBancarioCards } from "../components/demonstrativo-extrato-bancario-cards";
import { DemonstrativoExtratoBancarioTab, ItemCardProps } from "../demonstrativo-extrato-bancario.types";
import { useDemonstrativoExtratoBancario } from "../hooks/use-demonstrativo-extrato-bancario";
import { DEMONSTRATIVO_EXTRATO_BANCARIO_TABS } from "../constants/demonstrativo-extrato-bancario.constants";
import { useStatements, useBankAccounts } from "@/services/queries/useBankStatement";
import { useProjetoContext } from "@/context/projeto";

// Modals
import { ViewTransactionModal } from "./../components/modals/view-transaction-modal";
import { EditTransactionModal } from "./../components/modals/edit-transaction-modal";
import { DeleteTransactionModal } from "./../components/modals/delete-transaction-modal";
import { LaunchOperationModal } from "./../components/modals/launch-operation-modal";
import { BankStatementTransaction, BankStatementFilters } from "@/types/bank-statement";

export function DemonstrativoExtratoBancarioListView() {

  const { activeTab, setActiveTab } = useDemonstrativoExtratoBancario();
  const { projetoSelecionado } = useProjetoContext();

  // Account State
  const { data: bankAccounts, isLoading: isLoadingAccounts } = useBankAccounts();
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (bankAccounts?.data && bankAccounts.data.length > 0 && selectedAccountId === "") {
      setSelectedAccountId(bankAccounts.data[0].id.toString());
      setCurrentPage(1)
    }
  }, [bankAccounts, selectedAccountId]);

  // Pagination State
  const itemsPerPage = 10;

  const apiFilters: BankStatementFilters = {
    movieId: projetoSelecionado?.id,
    accountId: selectedAccountId ? Number(selectedAccountId) : undefined,
    transactionType: activeTab === "Entradas" ? "CREDIT" : activeTab === "Saídas" ? "DEBIT" : undefined,
    page: currentPage,
    limit: itemsPerPage,
  };

  const { data: statementsData, isLoading: isLoadingTransactions } = useStatements(apiFilters);
  const transactions: BankStatementTransaction[] = statementsData?.data || [];

  // Filter for Incentivos
  const filteredTransactions = activeTab === "Incentivos"
    ? transactions.filter(t => t.isIncentiveDeposit)
    : transactions;

  // Modals States
  const [selectedTransaction, setSelectedTransaction] = useState<BankStatementTransaction | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false);

  // Handlers
  const handleView = (transaction: BankStatementTransaction) => {
    setSelectedTransaction(transaction);
    setIsViewModalOpen(true);
  };

  const handleEdit = (transaction: BankStatementTransaction) => {
    setSelectedTransaction(transaction);
    setIsEditModalOpen(true);
  };

  const handleDelete = (transaction: BankStatementTransaction) => {
    setSelectedTransaction(transaction);
    setIsDeleteModalOpen(true);
  };

  const onSaveClassification = (newCategory: string) => {
    // Implement API call here with react-query or similar
    console.log(`Saving classification: ${newCategory} for tx: ${selectedTransaction?.id}`);
    setIsEditModalOpen(false);
  };

  const onConfirmDelete = () => {
    // Implement API call here
    console.log(`Deleting tx: ${selectedTransaction?.id}`);
    setIsDeleteModalOpen(false);
  };

  const onSaveNewOperation = (data: unknown) => {
    // Implement API call here to save
    console.log('Got new operation data:', data);
    setIsLaunchModalOpen(false);
  };

  const itensCard: ItemCardProps[] = [
    { label: "Incentivos", value: 0, classes: "text-[#f6f6f6]" },
    { label: "Entradas", value: 0, classes: "text-[#13d480]" },
    { label: "Saídas", value: 0, classes: "text-[#dc4426]" },
    { label: "Saldo Total", value: 0, classes: "text-[#f6f6f6]" }
  ];

  const isLoading = isLoadingAccounts || isLoadingTransactions;

  return (
    <AppContainer
      title="Demonstrativo de Extrato Bancário"
      description="Gerencie seus extratos bancários e transações"
    >
      <div className="flex flex-col gap-10 bg-darkblue rounded-[20px] p-6">
        <DemonstrativoExtratoBancarioHeaderButtons
          accounts={bankAccounts?.data || []}
          selectedAccountId={selectedAccountId}
          onAccountChange={setSelectedAccountId}
          onImportClick={() => console.log("Importar clicked")}
          onExportClick={() => console.log("Exportar clicked")}
          onLaunchOperationClick={() => setIsLaunchModalOpen(true)}
        />

        <DemonstrativoExtratoBancarioCards itensCard={itensCard} />

        <TabsUnderline
          tabs={[...DEMONSTRATIVO_EXTRATO_BANCARIO_TABS]}
          activeTab={activeTab}
          onTabChange={(tab) => {
            setActiveTab(tab as DemonstrativoExtratoBancarioTab);
            setCurrentPage(1);
          }}
          classes={{
            text: "text-base",
            button: "flex-1"
          }}
        />

        {isLoading ? (
          <div className="animate-pulse bg-darkblue rounded-lg h-80" />
        ) : (
          <BankStatementTableExtrato
            data={filteredTransactions}
            activeTab={activeTab}
            variant="demonstrativo"
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onFilterClick={() => console.log("Filter clicked")}
            onLaunchOperationClick={() => setIsLaunchModalOpen(true)}
            currentPage={currentPage}
            totalPages={statementsData?.pagination?.totalPages || 1}
            totalItems={statementsData?.pagination?.total || filteredTransactions.length}
            onPageChange={setCurrentPage}
          />
        )}

        {/* Modals */}
        <ViewTransactionModal 
          isOpen={isViewModalOpen} 
          onClose={() => setIsViewModalOpen(false)} 
          transaction={selectedTransaction} 
        />
        
        <EditTransactionModal 
          isOpen={isEditModalOpen} 
          onClose={() => setIsEditModalOpen(false)} 
          transaction={selectedTransaction}
          onSave={onSaveClassification}
        />

        <DeleteTransactionModal 
          isOpen={isDeleteModalOpen} 
          onClose={() => setIsDeleteModalOpen(false)} 
          transaction={selectedTransaction}
          onConfirm={onConfirmDelete}
        />

        <LaunchOperationModal
          isOpen={isLaunchModalOpen}
          onClose={() => setIsLaunchModalOpen(false)}
          onSave={onSaveNewOperation}
        />
      </div>
    </AppContainer>
  )
}